import { useCallback, useMemo } from "react";
import get from "lodash/get";
import hash from "object-hash";

import { IssuesFilter as IssuesFilterProto } from "@protos/issues/v1/issues";
import {
  EpssProbability,
  reachabilityFromJSON,
  reachabilityToJSON,
  Transitivity,
} from "@protos/sca/v1/sca";
import { SCA_FINDINGS_PAGE } from "@shared/constants";
import { useStateParams } from "@shared/hooks";
import { invert } from "@shared/utils/lib/invertObj";

import { useCommonIssuesFilterState } from "./useCommonIssuesFilterState";

/**
 *
 * @param obj an object mapping values of the desired type (which must extend string) to their string representation as a query param
 * @param config specifies the default value and default query parameter representation, used for unknown translations
 * @returns an object containing two functions:
 *  - `valueToQueryParam` accepts a value and returns a query parameter
 *  - `queryParamToValue` accepts a query parameter and returns a value
 */
function makeQueryParamTransforms<ValueType extends string>(
  queryParamsByValue: Record<ValueType, string>,
  config: { defaultValue: ValueType; defaultQueryParam: string }
) {
  const valuesByQueryParam = invert(queryParamsByValue);

  return {
    valueToQueryParam: (val: ValueType): string =>
      queryParamsByValue[val] ?? config.defaultQueryParam,
    queryParamToValue: (qp: string): ValueType =>
      get(valuesByQueryParam, qp, config.defaultValue),
  };
}

function transitivityToQueryParam(transitivity: Transitivity): string {
  switch (transitivity) {
    case Transitivity.DIRECT:
      return "direct";
    case Transitivity.TRANSITIVE:
      return "transitive";
    default:
      return "unknown";
  }
}

function transitivityFromQueryParam(queryParam: string): Transitivity {
  switch (queryParam) {
    case "direct":
      return Transitivity.DIRECT;
    case "transitive":
      return Transitivity.TRANSITIVE;
    default:
      return Transitivity.UNKNOWN_TRANSITIVITY;
  }
}

const EPSS_QUERY_PARAM_BY_CATEGORY = {
  [EpssProbability.LOW]: "low",
  [EpssProbability.MEDIUM]: "medium",
  [EpssProbability.HIGH]: "high",
  [EpssProbability.NONE]: "none",
  [EpssProbability.UNSPECIFIED]: "unspecified",
};

/**
 * Generates and manages state for any finding filters which apply to SCA issues.
 */
export const useScaIssuesFilterState = () => {
  // grab the filters which apply to all issue types ("common" filters)
  const commonFilters = useCommonIssuesFilterState(SCA_FINDINGS_PAGE);

  const epssTranslations = makeQueryParamTransforms<EpssProbability>(
    EPSS_QUERY_PARAM_BY_CATEGORY,
    {
      defaultQueryParam: "unspecified",
      defaultValue: EpssProbability.UNSPECIFIED,
    }
  );

  // set up the state for the various SCA filters
  const [reachabilities, setReachability] = useStateParams<
    IssuesFilterProto["reachabilities"]
  >(
    [],
    "reachability",
    (reachabilityVals) => reachabilityVals.map(reachabilityToJSON).join(","),
    (reachabilityStr) => reachabilityStr.split(",").map(reachabilityFromJSON)
  );

  const [transitivity, setTransitivity] = useStateParams<
    IssuesFilterProto["transitivities"]
  >(
    [],
    "transitivity",
    (transitivityVals) =>
      transitivityVals.map(transitivityToQueryParam).join(","),
    (transitivityStr) =>
      transitivityStr.split(",").map(transitivityFromQueryParam),
    SCA_FINDINGS_PAGE
  );

  const [dependencyQuery, setDependencyQuery] = useStateParams<
    IssuesFilterProto["dependencies"]
  >(
    [],
    "dependency",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    SCA_FINDINGS_PAGE
  );
  const [cveQuery, setCveQuery] = useStateParams<IssuesFilterProto["cves"]>(
    [],
    "cve",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    SCA_FINDINGS_PAGE
  );

  const [ruleIdQuery, setRuleIdQuery] = useStateParams<
    IssuesFilterProto["ruleIds"]
  >(
    [],
    "ruleId",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []).map((sid) => parseInt(sid)),
    SCA_FINDINGS_PAGE
  );

  const [epssQuery, setEpssQuery] = useStateParams<
    IssuesFilterProto["epssProbabilities"]
  >(
    [],
    "epssProbabilities",
    (categories) =>
      categories.map((c) => epssTranslations.valueToQueryParam(c)).join(","),
    (s) => s.split(",").map((s) => epssTranslations.queryParamToValue(s)),
    SCA_FINDINGS_PAGE
  );

  /**
   * A repackaged version of this state that is consumable by the issues API endpoint.
   */
  const issuesFilterProto = useMemo<IssuesFilterProto>(
    () => ({
      ...commonFilters.issuesFilterProto,
      cves: cveQuery,
      dependencies: dependencyQuery,
      reachabilities: reachabilities,
      transitivities: transitivity,
      ruleIds: ruleIdQuery,
      epssProbabilities: epssQuery,
    }),
    [
      commonFilters.issuesFilterProto,
      cveQuery,
      dependencyQuery,
      reachabilities,
      transitivity,
      ruleIdQuery,
      epssQuery,
    ]
  );

  /**
   * A hash of all the filters in this object. Useful to detect when any filters have changed
   * without needing to understand the internal state of this hook.
   */
  const filterHash = useMemo(
    () =>
      hash([
        commonFilters.filterHash,
        reachabilities,
        transitivity,
        dependencyQuery,
        cveQuery,
        ruleIdQuery,
        epssQuery,
      ]),
    [
      commonFilters.filterHash,
      cveQuery,
      dependencyQuery,
      reachabilities,
      transitivity,
      ruleIdQuery,
      epssQuery,
    ]
  );

  /**
   * `True` if any filter field is set to to non-default values, `False` otherwise.
   */
  const hasFiltersSet =
    commonFilters.hasFiltersSet ||
    reachabilities.length > 0 ||
    transitivity.length > 0 ||
    cveQuery.length > 0 ||
    dependencyQuery.length > 0 ||
    ruleIdQuery.length > 0 ||
    epssQuery.length > 0;

  /**
   * Clears all filter fields even if they are set by default
   */
  const clearFilters = useCallback(() => {
    commonFilters.clearFilters();
    setReachability([]);
    setTransitivity([]);
    setDependencyQuery([]);
    setCveQuery([]);
    setRuleIdQuery([]);
    setEpssQuery([]);
  }, [
    commonFilters,
    setCveQuery,
    setDependencyQuery,
    setReachability,
    setTransitivity,
    setRuleIdQuery,
    setEpssQuery,
  ]);

  /**
   * Resets all filter fields to their default values (empty, nullish, etc).
   */
  const resetFilters = useCallback(() => {
    commonFilters.resetFilters();
    setReachability([]);
    setTransitivity([]);
    setDependencyQuery([]);
    setCveQuery([]);
    setRuleIdQuery([]);
    setEpssQuery([]);
  }, [
    commonFilters,
    setCveQuery,
    setDependencyQuery,
    setReachability,
    setTransitivity,
    setRuleIdQuery,
    setEpssQuery,
  ]);

  return useMemo(
    () => ({
      repo: commonFilters.repo,
      setRepo: commonFilters.setRepo,
      repoTag: commonFilters.repoTag,
      setRepoTag: commonFilters.setRepoTag,
      repoRef: commonFilters.repoRef,
      setRepoRef: commonFilters.setRepoRef,
      team: commonFilters.team,
      setTeam: commonFilters.setTeam,
      tab: commonFilters.tab,
      severities: commonFilters.severities,
      setSeverities: commonFilters.setSeverities,
      setTab: commonFilters.setTab,
      aiVerdicts: commonFilters.aiVerdicts,
      setAiVerdicts: commonFilters.setAiVerdicts,
      aiSensitivity: commonFilters.aiSensitivity,
      setAiSensitivity: commonFilters.setAiSensitivity,
      ref: commonFilters.ref,
      setRef: commonFilters.setRef,
      primary: commonFilters.primary,
      setPrimary: commonFilters.setPrimary,
      issuesFilterProto,
      reachabilities,
      setReachability,
      transitivity,
      setTransitivity,
      dependencyQuery,
      setDependencyQuery,
      cveQuery,
      setCveQuery,
      ruleIdQuery,
      setRuleIdQuery,
      epssQuery,
      setEpssQuery,
      hasFiltersSet,
      clearFilters,
      resetFilters,
      filterHash,
    }),
    [
      commonFilters.repo,
      commonFilters.setRepo,
      commonFilters.repoTag,
      commonFilters.setRepoTag,
      commonFilters.repoRef,
      commonFilters.setRepoRef,
      commonFilters.team,
      commonFilters.setTeam,
      commonFilters.tab,
      commonFilters.severities,
      commonFilters.setSeverities,
      commonFilters.setTab,
      commonFilters.aiVerdicts,
      commonFilters.setAiVerdicts,
      commonFilters.aiSensitivity,
      commonFilters.setAiSensitivity,
      commonFilters.ref,
      commonFilters.setRef,
      commonFilters.primary,
      commonFilters.setPrimary,
      issuesFilterProto,
      reachabilities,
      setReachability,
      transitivity,
      setTransitivity,
      dependencyQuery,
      setDependencyQuery,
      cveQuery,
      setCveQuery,
      ruleIdQuery,
      setRuleIdQuery,
      epssQuery,
      setEpssQuery,
      hasFiltersSet,
      clearFilters,
      resetFilters,
      filterHash,
    ]
  );
};
